import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import {
  getStudentPracticeAttempts,
  StudentPracticeAttemptsResponse,
} from '../../services/lessonService';
import { FeedbackReview } from '../shared/FeedbackInbox';
import { FeedbackItem } from '../../services/feedbackService';

interface Props {
  open: boolean;
  studentId: string | null;
  studentName?: string;
  onClose: () => void;
}

const displayValue = (value: unknown) => {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return '—';
  return JSON.stringify(value);
};

const plainQuestion = (value: string) =>
  value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const StudentPracticeProgressDialog: React.FC<Props> = ({
  open,
  studentId,
  studentName,
  onClose,
}) => {
  const [progress, setProgress] = useState<StudentPracticeAttemptsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState<string | null>(null);

  const updateFeedback = (updated: FeedbackItem) => {
    setProgress((current) =>
      current
        ? {
            ...current,
            assignments: current.assignments.map((assignment) => ({
              ...assignment,
              attempts: assignment.attempts.map((attempt) =>
                attempt.attempt_id === updated.feedback_id
                  ? { ...attempt, feedback: updated }
                  : attempt,
              ),
            })),
          }
        : current,
    );
  };

  useEffect(() => {
    if (!open || !studentId) return;
    let active = true;
    setProgress(null);
    setError(null);
    setLoading(true);
    getStudentPracticeAttempts(studentId)
      .then((value) => active && setProgress(value))
      .catch((reason: unknown) => {
        if (active) {
          setError(reason instanceof Error ? reason.message : 'Failed to load practice attempts');
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [open, studentId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        {progress?.student_name || studentName
          ? `${progress?.student_name || studentName} — Practice progress`
          : 'Practice progress'}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !progress || progress.total_attempts === 0 ? (
          <Alert severity="info">This student has not attempted any practice questions yet.</Alert>
        ) : (
          progress.assignments
            .filter((assignment) => assignment.attempts.length > 0)
            .map((assignment) => (
              <Box key={assignment.assignment_id} sx={{ mb: 3 }}>
                <Typography variant="h6">{assignment.module_title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {assignment.module_subject || 'Subject not specified'} · Grade{' '}
                  {assignment.module_grade_level || 'not specified'}
                </Typography>
                {assignment.attempts.map((attempt, index) => (
                  <Box key={attempt.attempt_id} sx={{ py: 1.5 }}>
                    {index > 0 && <Divider sx={{ mb: 1.5 }} />}
                    <Box display="flex" justifyContent="space-between" gap={2}>
                      <Typography variant="subtitle2">{attempt.unit_title}</Typography>
                      <Chip
                        size="small"
                        label={
                          attempt.feedback
                            ? attempt.feedback.publication_status === 'published'
                              ? 'AI feedback published'
                              : `${
                                  attempt.feedback.status
                                } · ${attempt.feedback.publication_status.replace('_', ' ')}`
                            : attempt.is_correct === null
                            ? 'AI feedback unavailable'
                            : attempt.is_correct
                            ? 'Correct'
                            : 'Incorrect'
                        }
                        color={
                          attempt.is_correct === null
                            ? 'info'
                            : attempt.is_correct
                            ? 'success'
                            : 'error'
                        }
                      />
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Question:</strong> {plainQuestion(attempt.question)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      <strong>Student answer:</strong> {displayValue(attempt.student_answer)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Attempted {new Date(attempt.attempted_at).toLocaleString()}
                    </Typography>
                    {attempt.feedback && (
                      <>
                        <Button
                          size="small"
                          sx={{ mt: 1 }}
                          onClick={() =>
                            setReviewing((current) =>
                              current === attempt.attempt_id ? null : attempt.attempt_id,
                            )
                          }
                        >
                          {reviewing === attempt.attempt_id
                            ? 'Hide AI feedback'
                            : attempt.feedback.publication_status === 'published'
                            ? 'View AI feedback'
                            : 'Review AI feedback'}
                        </Button>
                        <Collapse in={reviewing === attempt.attempt_id}>
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <FeedbackReview
                              key={`${attempt.feedback.feedback_id}-${attempt.feedback.revision}`}
                              item={attempt.feedback}
                              onUpdate={updateFeedback}
                              showContext={false}
                            />
                          </Box>
                        </Collapse>
                      </>
                    )}
                  </Box>
                ))}
              </Box>
            ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
